import React from 'react';
import { View, ScrollView, RefreshControl } from 'react-native';
import { Text } from '@shared/components/text';
import { Head } from '@shared/components/head';
import Button from '@shared/components/md-design-system/Button/Button';
import { Divider } from '@shared/components/md-design-system/Layout';
import { Loading } from '@shared/components/loading';
import MainErrorWithIcon from '@shared/components/ModularInfoWithIcon';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { useAuth } from 'app/auth/hooks/useAuth';
import { YearFilterDropdown } from './filters/YearFilterDropdown';
import { MonthFilterDropdown } from './filters/MonthFilterDropdown';
import { PatientDocumentCard } from './documents/PatientDocumentCard';
import { TelemonitoringReportCard } from './reports/ReportCard';
import { AppointmentReportCard } from './documents/AppointmentReportCard';
import { UploadDocumentModal } from './documents/UploadDocumentModal';
import { DeleteConfirmationModal } from './documents/DeleteConfirmationModal';
import { useDocumentDownload } from '../hooks/useDocumentDownload';
import { useDocumentFilters } from '../hooks/useDocumentFilters';
import { useDocumentUpload } from '../hooks/useDocumentUpload';
import { useDocumentDelete } from '../hooks/useDocumentDelete';
import { usePatientHistoryItems } from '../hooks/usePatientHistoryItems';
import { groupItemsByMonth } from '../utils/documentUtils';

export const PatientDocumentsSection: React.FC = () => {
  const { t } = dynamicTranslations();
  const { meData } = useAuth();
  const pathwayId = meData?.current_pathway?.id ?? '';

  const filters = useDocumentFilters();
  const upload = useDocumentUpload(pathwayId);
  const deleteDoc = useDocumentDelete(pathwayId);
  const { openDocument, downloadingId } = useDocumentDownload(pathwayId);

  const { items, isLoading, isRefetching, refetch } = usePatientHistoryItems(
    pathwayId,
    filters.createdAtRanges,
  );

  const groupedItems = groupItemsByMonth(items);

  return (
    <ScrollView
      className="flex-1"
      refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refetch} />}
    >
      <View className="mb-4">
        <Head type="h2" className="text-gray-900 mb-1">
          {t('history.documents.patientSection.title')}
        </Head>
        <Text className="text-sm text-gray-600 mb-4">
          {t('history.documents.patientSection.subtitle')}
        </Text>
        <View className="self-start">
          <Button
            icon="upload"
            variant="outline"
            onPress={() => upload.setIsUploadModalVisible(true)}
            disabled={upload.isUploading}
          >
            {upload.isUploading
              ? t('history.documents.patientSection.uploading')
              : t('history.documents.patientSection.uploadButton')}
          </Button>
        </View>
      </View>

      <Divider />

      <View className="flex-row mt-4 mb-4 gap-3">
        <YearFilterDropdown
          selectedYears={filters.selectedYears}
          onYearsChange={filters.setSelectedYears}
          onApplyFilter={filters.onApplyYear}
          onClearFilter={filters.onClearYear}
          isFilterApplied={filters.isYearFilterApplied}
        />
        <MonthFilterDropdown
          selectedMonths={filters.selectedMonths}
          onMonthsChange={filters.setSelectedMonths}
          onApplyFilter={filters.onApplyMonth}
          onClearFilter={filters.onClearMonth}
          isFilterApplied={filters.isMonthFilterApplied}
        />
      </View>

      {isLoading ? (
        <View className="h-48">
          <Loading />
        </View>
      ) : items.length === 0 ? (
        <MainErrorWithIcon
          errorType={filters.isFiltering ? 'no_documents_filtered' : 'no_documents'}
        />
      ) : (
        Object.entries(groupedItems).map(([monthYear, monthItems]) => (
          <View key={monthYear} className="mb-6">
            <Text className="text-xl font-semibold mb-4 text-gray-900">{monthYear}</Text>
            {monthItems.map(item =>
              item.kind === 'document' ? (
                <PatientDocumentCard
                  key={`doc-${item.data.id}`}
                  document={item.data}
                  onView={() =>
                    openDocument(item.data.id, item.data.name || item.data.originalFileName, item.data.fileType)
                  }
                  onDelete={() => deleteDoc.setDocumentToDelete(item.data)}
                  isViewing={downloadingId === item.data.id}
                />
              ) : item.kind === 'report' ? (
                <TelemonitoringReportCard
                  key={`report-${item.data.id}`}
                  reportData={{
                    id: item.data.id,
                    title: item.data.reportName,
                    startDate: item.data.periodStartDate,
                    endDate: item.data.periodEndDate,
                    receivedDate: item.data.createdAt,
                    status: item.data.status,
                  }}
                  openedByPatient
                />
              ) : (
                <AppointmentReportCard
                  key={`appointment-report-${item.data.id}`}
                  report={item.data}
                />
              ),
            )}
          </View>
        ))
      )}

      <UploadDocumentModal
        visible={upload.isUploadModalVisible}
        onClose={() => upload.setIsUploadModalVisible(false)}
        onSelectCamera={upload.handleCameraUpload}
        onSelectExternalDevice={upload.handleExternalDeviceUpload}
      />

      {deleteDoc.documentToDelete && (
        <DeleteConfirmationModal
          visible
          documentName={
            deleteDoc.documentToDelete.name || deleteDoc.documentToDelete.originalFileName
          }
          isDeleting={deleteDoc.isDeleting}
          onConfirm={deleteDoc.handleDeleteConfirm}
          onCancel={() => deleteDoc.setDocumentToDelete(null)}
        />
      )}
    </ScrollView>
  );
};
